/**
 * Declarative page title configuration.
 *
 * Each entry: { match: string | RegExp, title: string }
 * - String match: prefix-matches the pathname (exact or /path/...)
 * - RegExp match: tested against the full pathname
 * Entries are matched top-to-bottom; the first match wins.
 * More specific paths must come before less specific prefixes.
 */
export const ROUTE_TITLES = [
  // Super admin routes
  { match: '/superadmin/analytics/audit', title: 'Audit Log' },
  { match: '/superadmin/analytics/logins', title: 'Login Analytics' },
  { match: '/superadmin/facilities', title: 'Facilities' },
  { match: '/superadmin/users', title: 'Users' },
  { match: '/superadmin/roles', title: 'Role Permissions' },
  { match: '/superadmin/config', title: 'Platform Config' },
  { match: '/superadmin/facility-location', title: 'Ground Location' },

  // Dashboard — asset management (specific before generic)
  { match: '/dashboard/assets/inventory/new', title: 'Add Inventory Item' },
  { match: /\/dashboard\/assets\/inventory\//, title: 'Inventory Details' },
  { match: '/dashboard/assets/inventory', title: 'Inventory' },
  { match: '/dashboard/assets/fixed/new', title: 'Add Fixed Asset' },
  { match: /\/dashboard\/assets\/fixed\/.*\/edit/, title: 'Edit Fixed Asset' },
  { match: /\/dashboard\/assets\/fixed\//, title: 'Fixed Asset Details' },
  { match: '/dashboard/assets/fixed', title: 'Fixed Assets' },
  { match: '/dashboard/assets', title: 'Asset Management' },
  // Dashboard — operations (specific before generic)
  { match: '/dashboard/operations/daily', title: 'Daily Operations' },
  { match: '/dashboard/operations/equipment/new', title: 'Add Equipment' },
  { match: /\/dashboard\/operations\/equipment\//, title: 'Equipment Details' },
  { match: '/dashboard/operations/equipment', title: 'Equipment List' },
  { match: '/dashboard/operations', title: 'Operations' },

  // Dashboard — staff & selfie attendance
  { match: '/dashboard/staff/attendance', title: 'Attendance' },
  { match: '/dashboard/selfie-attendance/records', title: 'Selfie Attendance Records' },
  { match: '/dashboard/selfie-attendance/history', title: 'Selfie History' },
  { match: '/dashboard/selfie-attendance', title: 'Selfie Attendance' },
  { match: '/dashboard/staff/salary', title: 'Salary' },
  { match: /\/dashboard\/staff\//, title: 'Staff Details' },
  { match: '/dashboard/staff', title: 'Staff Management' },

  // Dashboard — expenses
  { match: '/dashboard/expenses/new', title: 'Add Expense' },
  { match: '/dashboard/expenses/categories', title: 'Expense Categories' },
  { match: '/dashboard/expenses/analytics', title: 'Expense Analytics' },
  { match: /\/dashboard\/expenses\//, title: 'Expense Details' },
  { match: '/dashboard/expenses', title: 'Expenses' },

  // Dashboard — bookings
  { match: '/dashboard/bookings-overview', title: 'Book Now' },
  { match: '/dashboard/book-now', title: 'Book Now' },
  { match: '/dashboard/my-bookings', title: 'My Bookings' },
  { match: '/dashboard/booking-requests', title: 'Booking Requests' },
  { match: /\/dashboard\/booking-new/, title: 'New Booking' },
  { match: /\/dashboard\/booking\//, title: 'New Booking' },
  { match: '/dashboard/active-slots', title: 'Booked Slots' },

  // Dashboard — finance & reports
  { match: '/dashboard/payments', title: 'Payments' },
  { match: '/dashboard/reports', title: 'Reports' },

  // Dashboard — support
  { match: '/dashboard/help-center', title: 'Help Center' },
  { match: '/dashboard/create-ticket', title: 'Create Ticket' },

  // Dashboard — admin
  { match: '/dashboard/profile', title: 'My Profile' },
  { match: '/dashboard/users', title: 'Users' },
  { match: '/dashboard/roles', title: 'Role Permissions' },

  // Dashboard — facility management
  { match: '/dashboard/grounds', title: 'Grounds' },
  { match: '/dashboard/configure-service', title: 'Configure a Service' },
  { match: '/dashboard/facility-services', title: 'Facility Services' },
  { match: '/dashboard/facility-location', title: 'Ground Location' },
  { match: '/dashboard/waitlist', title: 'Waitlist' },
  { match: '/dashboard/tournaments/new', title: 'Register Tournament' },
  { match: /\/dashboard\/tournaments\/.*\/edit/, title: 'Edit Tournament' },
  { match: /\/dashboard\/tournaments\//, title: 'Tournament Details' },
  { match: '/dashboard/tournaments', title: 'Tournaments' },

  // Dashboard root
  { match: '/dashboard', title: 'Home' },
];

/**
 * Resolve a page title from a pathname.
 * Falls back to prettifying the last URL segment.
 *
 * @param {string} pathname
 * @returns {string}
 */
export function getPageTitle(pathname) {
  for (const { match, title } of ROUTE_TITLES) {
    if (typeof match === 'string') {
      if (pathname === match || pathname.startsWith(match + '/')) {
        return title;
      }
    } else if (match instanceof RegExp) {
      if (match.test(pathname)) return title;
    }
  }

  // Fallback: prettify last URL segment
  const seg = pathname.split('/').filter(Boolean).slice(-1)[0] || '';
  if (!seg) return 'Dashboard';
  return seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}
